import { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useLang } from "@/lib/i18n";

const WIDTH = 320;
const HEIGHT = 190;
const PAD = 34;
const TILE = 256;
const MIN_ZOOM = 9;
const MAX_ZOOM = 16;
const MAX_LAT = 85.05112878;

const worldPixel = ([lon, lat], zoom) => {
  const safeLat = Math.max(-MAX_LAT, Math.min(MAX_LAT, lat));
  const n = 2 ** zoom;
  const rad = (safeLat * Math.PI) / 180;
  return {
    x: ((lon + 180) / 360) * n * TILE,
    y: ((1 - Math.asinh(Math.tan(rad)) / Math.PI) / 2) * n * TILE,
  };
};

export default function RoutePreviewMap({ geometry }) {
  const { t } = useLang();
  const reduceMotion = useReducedMotion();

  const model = useMemo(() => {
    const coordinates = geometry?.coordinates || [];
    if (coordinates.length < 2) return null;
    let zoom = MIN_ZOOM;
    let pixels = [];
    let scale = 1;
    for (let candidate = MAX_ZOOM; candidate >= MIN_ZOOM; candidate -= 1) {
      pixels = coordinates.map((coord) => worldPixel(coord, candidate));
      const spanX = Math.max(Math.max(...pixels.map((p) => p.x)) - Math.min(...pixels.map((p) => p.x)), 1);
      const spanY = Math.max(Math.max(...pixels.map((p) => p.y)) - Math.min(...pixels.map((p) => p.y)), 1);
      scale = Math.min((WIDTH - PAD * 2) / spanX, (HEIGHT - PAD * 2) / spanY);
      zoom = candidate;
      if (scale >= 1) break;
    }
    scale = Math.min(scale, 1.15);
    const minX = Math.min(...pixels.map((p) => p.x));
    const maxX = Math.max(...pixels.map((p) => p.x));
    const minY = Math.min(...pixels.map((p) => p.y));
    const maxY = Math.max(...pixels.map((p) => p.y));
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const toScreen = (p) => [
      WIDTH / 2 + (p.x - centerX) * scale,
      HEIGHT / 2 + (p.y - centerY) * scale,
    ];
    const points = pixels.map(toScreen);
    const path = points.map(([x, y], index) => `${index === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");

    const worldLeft = centerX - WIDTH / (2 * scale);
    const worldRight = centerX + WIDTH / (2 * scale);
    const worldTop = centerY - HEIGHT / (2 * scale);
    const worldBottom = centerY + HEIGHT / (2 * scale);
    const count = 2 ** zoom;
    const tiles = [];
    for (let y = Math.floor(worldTop / TILE) - 1; y <= Math.floor(worldBottom / TILE) + 1; y += 1) {
      for (let x = Math.floor(worldLeft / TILE) - 1; x <= Math.floor(worldRight / TILE) + 1; x += 1) {
        if (y < 0 || y >= count) continue;
        const wrappedX = ((x % count) + count) % count;
        tiles.push({
          key: `${zoom}-${wrappedX}-${y}`,
          url: `https://tile.openstreetmap.org/${zoom}/${wrappedX}/${y}.png`,
          left: WIDTH / 2 + (x * TILE - centerX) * scale,
          top: HEIGHT / 2 + (y * TILE - centerY) * scale,
          size: TILE * scale,
        });
      }
    }
    return { path, start: points[0], end: points[points.length - 1], tiles };
  }, [geometry]);

  const hasRoute = Boolean(model?.path);
  const routeTransition = reduceMotion
    ? { duration: 0 }
    : { duration: 1.15, ease: [0.22, 1, 0.36, 1] };

  return (
    <div className="relative overflow-hidden rounded-[1.4rem] border border-white/10 bg-[#07142F]" data-testid="booking-route-map">
      <div className="absolute inset-0 overflow-hidden bg-[#0B1F3D]" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(36,87,255,0.14),transparent_60%),radial-gradient(circle_at_75%_70%,rgba(10,38,99,0.5),transparent_65%)]" />
        {model?.tiles.map((tile) => (
          <img
            key={tile.key}
            src={tile.url}
            alt=""
            loading="lazy"
            decoding="async"
            referrerPolicy="strict-origin-when-cross-origin"
            className="absolute max-w-none select-none opacity-70 saturate-[0.35] contrast-125 brightness-[0.62]"
            style={{
              left: `${(tile.left / WIDTH) * 100}%`,
              top: `${(tile.top / HEIGHT) * 100}%`,
              width: `${(tile.size / WIDTH) * 100}%`,
              height: `${(tile.size / HEIGHT) * 100}%`,
            }}
            onError={(event) => { event.currentTarget.style.display = "none"; }}
          />
        ))}
        <div className="absolute inset-0 bg-[#07142F]/45" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_42%,rgba(36,87,255,0.18),transparent_62%)]" />
      </div>

      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="relative block h-48 w-full" role="img" aria-label={t("bk.route_map_alt")}>
        <defs>
          <filter id="routeBlur" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="5" />
          </filter>
        </defs>
        {hasRoute ? (
          <>
            <motion.path
              key={`glow-${model.path.length}`}
              d={model.path}
              fill="none"
              stroke="#2457FF"
              strokeWidth="10"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.34"
              filter="url(#routeBlur)"
              initial={{ pathLength: reduceMotion ? 1 : 0 }}
              animate={{ pathLength: 1 }}
              transition={routeTransition}
            />
            <motion.path
              key={model.path}
              d={model.path}
              fill="none"
              stroke="#56B8FF"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: reduceMotion ? 1 : 0 }}
              animate={{ pathLength: 1 }}
              transition={routeTransition}
              data-testid="booking-route-path"
            />
            <g>
              <circle cx={model.start[0]} cy={model.start[1]} r="10" fill="#2457FF" opacity="0.24" />
              <circle cx={model.start[0]} cy={model.start[1]} r="5" fill="#56B8FF" stroke="#FFFFFF" strokeWidth="2" />
            </g>
            <g>
              <circle cx={model.end[0]} cy={model.end[1]} r="11" fill="#FFFFFF" opacity="0.18" />
              <circle cx={model.end[0]} cy={model.end[1]} r="5" fill="#FFFFFF" stroke="#2457FF" strokeWidth="2.5" />
            </g>
          </>
        ) : (
          <>
            <circle cx="160" cy="95" r="46" fill="none" stroke="#56B8FF" strokeOpacity="0.22" strokeWidth="2" strokeDasharray="5 8" />
            <circle cx="160" cy="95" r="7" fill="#56B8FF" stroke="#FFFFFF" strokeWidth="2" />
            <path d="M160 60 A35 35 0 0 1 195 95" fill="none" stroke="#FFFFFF" strokeOpacity="0.55" strokeWidth="3" strokeLinecap="round" />
          </>
        )}
      </svg>

      <a
        href="https://www.openstreetmap.org/copyright"
        target="_blank"
        rel="noreferrer"
        data-testid="route-map-attribution"
        className="absolute right-2 top-2 rounded-full bg-miles-deep/60 px-2 py-1 text-[9px] text-white/45 backdrop-blur-sm transition-colors hover:text-white/80"
      >
        © OSM
      </a>
    </div>
  );
}
